import moment from "moment-timezone";

/**
 * Label format constants for timezone display
 * @readonly
 * @enum {string}
 */
export const LABEL_FORMATS = Object.freeze({
  CODE: "code", // Timezone code (e.g., "America/New_York")
  NAME: "name", // Timezone name (e.g., "New York")
  OFFSET: "offset", // UTC offset (e.g., "-05:00")
  NAME_WITH_OFFSET: "nameWithOffset", // Name with offset (e.g., "New York (-05:00)")
  NAME_WITH_UTC: "nameWithUTC", // Name with UTC (e.g., "New York (UTC-05:00)")
  ABBR: "abbr", // Timezone abbreviation (e.g., "EST")
  FULL: "full", // Full format (e.g., "New York (EST) - America/New_York (UTC-05:00)")
  ABBR_WITH_OFFSET: "abbrWithOffset", // Abbreviation with offset (e.g., "EST (UTC-05:00)")
});

/**
 * Deprecated/old timezone names to filter out from dropdown
 * These are kept in IANA database for backward compatibility but shouldn't be shown to users
 */
const DEPRECATED_TIMEZONES = [
  // "Asia/Calcutta", // Use Asia/Kolkata instead
  "Asia/Katmandu", // Use Asia/Kathmandu instead
  "Asia/Rangoon", // Use Asia/Yangon instead
  "Asia/Saigon", // Use Asia/Ho_Chi_Minh instead
  "America/Buenos_Aires", // Use America/Argentina/Buenos_Aires instead
  "America/Godthab", // Use America/Nuuk instead
  "Atlantic/Faeroe", // Use Atlantic/Faroe instead
  "Pacific/Ponape", // Use Pacific/Pohnpei instead
  "Pacific/Truk", // Use Pacific/Chuuk instead
  "Pacific/Yap", // Use Pacific/Chuuk instead
  "Europe/Kiev", // Use Europe/Kyiv instead
  "US/Eastern", // Use America/New_York instead
  "US/Central", // Use America/Chicago instead
  "US/Mountain", // Use America/Denver instead
  "US/Pacific", // Use America/Los_Angeles instead
];

/**
 * Detect the user's browser timezone
 * @returns {string} IANA timezone code (e.g., "America/New_York")
 */
export const detectBrowserTimezone = () => {
  try {
    // Try native Intl API first
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (error) {
    // Fallback to moment-timezone
    return moment.tz.guess();
  }
};

/**
 * Get UTC offset for a timezone
 * @param {string} timezone - IANA timezone code
 * @returns {string} UTC offset (e.g., "-05:00" or "+05:30")
 */
export const getTimezoneOffset = timezone => {
  try {
    return moment.tz(timezone).format("Z");
  } catch (error) {
    return "";
  }
};

/**
 * Get timezone abbreviation
 * @param {string} timezone - IANA timezone code
 * @returns {string} Timezone abbreviation (e.g., "EST", "PST")
 */
export const getTimezoneAbbreviation = timezone => {
  try {
    return moment.tz(timezone).format("z");
  } catch (error) {
    return "";
  }
};

/**
 * Get current time in a specific timezone
 * @param {string} timezone - IANA timezone code
 * @param {string} format - Time format (default: "h:mm A")
 * @returns {string} Formatted time (e.g., "3:45 PM")
 */
export const getCurrentTimeInTimezone = (timezone, format = "h:mm A") => {
  try {
    return moment.tz(timezone, timezone).format(format);
  } catch (error) {
    return "";
  }
};

/**
 * Extract region from timezone code
 * @param {string} timezone - IANA timezone code (e.g., "America/New_York")
 * @returns {string} Region name (e.g., "Americas")
 */
export const getTimezoneRegion = timezone => {
  if (!timezone) return "Other";

  const regionMap = {
    Africa: "Africa",
    America: "Americas",
    Antarctica: "Antarctica",
    Arctic: "Arctic",
    Asia: "Asia",
    Atlantic: "Atlantic",
    Australia: "Australia/Oceania",
    Europe: "Europe",
    Indian: "Indian Ocean",
    Pacific: "Pacific",
    UTC: "UTC",
    Etc: "Other",
  };

  const region = timezone.split("/")[0];
  return regionMap[region] || "Other";
};

/**
 * Format timezone name for display
 * @param {string} timezone - IANA timezone code
 * @returns {string} Human-readable name (e.g., "New York" from "America/New_York")
 */
export const formatTimezoneName = timezone => {
  if (!timezone) return "";

  // Split by "/" and get the last part
  const parts = timezone.split("/");
  const cityName = parts[parts.length - 1];

  // Replace underscores with spaces
  return cityName.replace(/_/g, " ");
};

/**
 * Build complete timezone object with all metadata
 * @param {string} timezone - IANA timezone code
 * @returns {object} Timezone object with all metadata
 */
export const buildTimezoneObject = timezone => {
  const offset = getTimezoneOffset(timezone);
  const abbr = getTimezoneAbbreviation(timezone);
  const region = getTimezoneRegion(timezone);
  const name = formatTimezoneName(timezone);

  return {
    code: timezone,
    name,
    region,
    offset,
    abbr,
    label: timezone, // Default label, can be customized
  };
};

/**
 * Get all timezones with metadata
 * @param {boolean} includeDeprecated - Whether to include deprecated timezone names (default: false)
 * @returns {array} Array of timezone objects
 */
export const getAllTimezones = (includeDeprecated = false) => {
  let allTimezones = moment.tz.names();

  // Filter out deprecated/old timezone names unless explicitly requested
  if (!includeDeprecated) {
    allTimezones = allTimezones.filter(tz => !DEPRECATED_TIMEZONES.includes(tz));
  }

  return allTimezones.map(tz => buildTimezoneObject(tz));
};

/**
 * Get timezones by region
 * @param {string} region - Region name (e.g., "Americas")
 * @returns {array} Array of timezone objects
 */
export const getTimezonesByRegion = region => {
  const allTimezones = getAllTimezones();
  return allTimezones.filter(tz => tz.region === region);
};

/**
 * Format timezone label based on format type
 * @param {object} timezone - Timezone object
 * @param {string} format - Format type
 * @returns {string} Formatted label
 */
export const formatTimezoneLabel = (timezone, format = "name") => {
  const { code, name, offset, abbr } = timezone;

  const formatters = {
    code: () => code,
    name: () => name,
    offset: () => `${code} (GMT${offset})`,
    nameWithOffset: () => `${name} (GMT${offset})`,
    nameWithUTC: () => `${code} (UTC${offset})`,
    abbr: () => `${name} (${abbr})`,
    full: () => `${name} (${abbr}) - ${code} (UTC${offset})`,
    abbrWithOffset: () => `${abbr} (UTC${offset})`,
  };

  return formatters[format] ? formatters[format]() : code;
};

/**
 * Popular/Common timezones list
 */
export const POPULAR_TIMEZONES = [
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Phoenix",
  "Pacific/Honolulu",
  "America/Anchorage",
  "Europe/London",
  "Europe/Paris",
  "Europe/Berlin",
  "Asia/Dubai",
  "Asia/Kolkata",
  "Asia/Singapore",
  "Asia/Tokyo",
  "Asia/Shanghai",
  "Australia/Sydney",
  "Pacific/Auckland",
];

/**
 * US-specific timezones
 */
export const US_TIMEZONES = [
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Phoenix",
  "America/Anchorage",
  "Pacific/Honolulu",
];

/**
 * Group timezones by region
 * @param {array} timezones - Array of timezone objects
 * @returns {object} Object with regions as keys
 */
export const groupTimezonesByRegion = timezones => {
  return timezones.reduce((acc, tz) => {
    const { region } = tz;
    if (!acc[region]) {
      acc[region] = [];
    }
    acc[region].push(tz);
    return acc;
  }, {});
};
